import {isDate, toDate} from 'date-fns'
import React from 'react'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {RangePoint} from 'yougile-model'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {BooleanHookState} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import type {DateRange} from './date-picker'
import {MonthCalendar} from './month-calendar'
import {DateTextInput} from './text-input/date-text-input'
import {TimeTextInput} from './text-input/time-text-input'
import {getTimePart, replaceTimePart, TimePart} from './util'

interface Props {
  dateRange: DateRange
  rangePoint: RangePoint
  onChange: (range: DateRange) => void
  onOpenChange?: (isOpen: boolean) => void
  onDateValidate?: (isValid: boolean) => void
  showLabel?: boolean
  showTimeInput?: boolean
  disablePastDates?: boolean
  disableSetDeadlineBeforeStart?: boolean
  valid?: BooleanHookState
  blocked?: boolean
}

const NO_TIME_DEFAULT_HOURS = 12

export const PickerSection = (props: Props) => {
  const {
    dateRange,
    rangePoint,
    showLabel,
    showTimeInput,
    onOpenChange,
    onDateValidate,
    disablePastDates,
    disableSetDeadlineBeforeStart,
    valid,
    blocked
  } = props
  const dateRangePoint = dateRange[rangePoint]
  const withoutTime = !showTimeInput && dateRangePoint && isDate(dateRangePoint)

  const activeDate = withoutTime
    ? new Date(dateRangePoint.setUTCHours(NO_TIME_DEFAULT_HOURS, 0, 0, 0))
    : dateRangePoint
  const labelText =
    rangePoint === 'start' ? Lang.msg.datePickerFrom : Lang.msg.datePickerTill
  const onChange = (value: Date | TimePart) => {
    const currentDate =
      showTimeInput || activeDate
        ? activeDate
        : new Date(new Date().setUTCHours(NO_TIME_DEFAULT_HOURS, 0, 0, 0))
    const nextDate = getNextDate(value, currentDate, rangePoint)
    if (nextDate) {
      if (disablePastDates && nextDate < toDate(Time.now())) {
        return
      }
      if (disableSetDeadlineBeforeStart && rangePoint === 'end') {
        if (dateRange.start && nextDate < dateRange.start) {
          return
        }
      }
      props.onDateValidate?.(valid?.value ?? true)
      props.onChange(getNextRange(nextDate, dateRange, rangePoint))
    }
  }
  return (
    <div className="inline-block p-8 relative isolate">
      {blocked && (
        <Tooltip content={Lang.msg.datePickerBlockedRangeStart}>
          <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center z-10">
            <Icon icon="IconLock" size={24} color="acid-red" />
          </div>
        </Tooltip>
      )}
      <div className={cn(blocked && 'opacity-20 pointer-events-none')}>
        <div className="flex items-center justify-center pb-10 gap-x-8">
          {showLabel && (
            <span className="font-semibold text-12 text-third-old">
              {labelText}
            </span>
          )}
          <DateTextInput
            initialValue={activeDate}
            onChange={onChange}
            onValidate={onDateValidate}
            disablePastDates={disablePastDates}
            valid={valid}
          />
          {showTimeInput && (
            <TimeTextInput
              initialValue={getInitialTimePart(activeDate, rangePoint)}
              onChange={onChange}
              onOpenChange={onOpenChange}
              disabled={!activeDate}
            />
          )}
        </div>
        <MonthCalendar
          dateRange={dateRange}
          rangePoint={rangePoint}
          onSelect={onChange}
          disablePastDates={disablePastDates}
        />
      </div>
    </div>
  )
}

const getNextDate = (
  value: Date | TimePart,
  currentDate: Date | undefined,
  rangePoint: RangePoint
): Date | undefined => {
  if ('getTime' in value) {
    return replaceTimePart(value, getInitialTimePart(currentDate, rangePoint))
  } else if (currentDate) {
    return replaceTimePart(currentDate, value)
  }
  return undefined
}

const getInitialTimePart = (
  date: Date | undefined,
  rangePoint: RangePoint
): TimePart => {
  if (date) {
    return getTimePart(date)
  }
  return rangePoint === 'start'
    ? {hours: 0, minutes: 0}
    : {hours: 23, minutes: 59}
}

const getNextRange = (
  value: Date,
  currentRange: DateRange,
  point: RangePoint
): DateRange => {
  const {start, end} = currentRange
  if (point === 'start' && end && value > end) {
    const newEnd = replaceTimePart(value, getTimePart(end))
    return {start: value, end: newEnd > value ? newEnd : value}
  }
  if (point === 'end' && start && value < start) {
    const newStart = replaceTimePart(value, getTimePart(start))
    return {start: newStart < value ? newStart : value, end: value}
  }
  return {...currentRange, ...{[point]: value}}
}
